import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedSection } from '../ui/AnimatedSection';
import { Sword, Factory, Hammer } from 'lucide-react';
import { Slideshow } from '../ui/Slideshow';
import { API_ENDPOINTS } from '../../config/api';

// Define static image paths
const gamemode1 = '/images/gamemode-1.webp';
const gamemode2 = '/images/gamemode-2.webp';
const gamemode3 = '/images/gamemode-3.webp';

interface GamemodeImages {
  [key: string]: { image_url: string }[];
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  startDate?: string;
  image?: string;
  dynamicImages?: string[];
  isReversed?: boolean;
}

function FeatureCard({ 
  icon, 
  title, 
  description, 
  startDate,
  image, 
  dynamicImages = [], 
  isReversed = false 
}: FeatureCardProps) {
  const { t } = useTranslation();
  const contentAnimation = isReversed ? "fade-left" : "fade-right";
  const imageAnimation = isReversed ? "fade-right" : "fade-left";

  // Create array of images, using dynamic images first, then fallback
  const displayImages = dynamicImages.length > 0 ? dynamicImages : (image ? [image] : []);

  return (
    <div className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-center`}>
      {/* Content Side */}
      <AnimatedSection
        animation={contentAnimation}
        className="flex-1 transition-all duration-700"
        threshold={0.2}
      >
        <div className="relative group max-w-xl mx-auto md:mx-0">
          <div className="relative bg-gray-900 p-8 rounded-lg border border-gray-800 hover:border-gray-700 transition-colors">
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-500/10 rounded-lg">
                <div className="text-primary-500">{icon}</div>
              </div>
              <h3 className="text-2xl font-bold text-white">{title}</h3>
            </div>
            <p className="text-lg text-gray-400 leading-relaxed">{description}</p>
            {startDate && (
              <p className="text-sm text-gray-500 mt-4">
                <span className="text-primary-400">{t('features.runningSince')}</span>: {startDate}
              </p>
            )}
          </div>
        </div>
      </AnimatedSection>

      {/* Image Side */}
      {displayImages.length > 0 && (
        <AnimatedSection
          animation={imageAnimation}
          className="flex-1 transition-all duration-700"
          threshold={0.2}
        >
          <div className="relative">
            {displayImages.length > 1 ? (
              <Slideshow
                images={displayImages}
                interval={5000}
                className="w-full max-w-xl mx-auto"
              />
            ) : (
              <img 
                src={displayImages[0]} 
                alt={title}
                className="relative rounded-xl shadow-2xl w-full max-w-xl mx-auto"
              />
            )}
          </div>
        </AnimatedSection>
      )}
    </div>
  );
}

export function Features() {
  const { t } = useTranslation();
  const [gamemodeImages, setGamemodeImages] = useState<GamemodeImages>({});

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.gamemodes.images);
        if (!response.ok) throw new Error('Failed to fetch images');
        const data = await response.json();
        setGamemodeImages(data);
      } catch (error) {
        console.error('Error fetching gamemode images:', error);
      }
    };

    fetchImages();
  }, []);

  const features = [
    {
      icon: <Sword className="w-6 h-6" />,
      title: t('features.1.title'),
      description: t('features.1.description'),
      startDate: t('features.1.startDate'),
      image: gamemode1,
      dynamicImages: gamemodeImages.survival?.map(img => img.image_url) || [],
    },
    {
      icon: <Factory className="w-6 h-6" />,
      title: t('features.2.title'),
      description: t('features.2.description'),
      startDate: t('features.2.startDate'),
      image: gamemode2,
      dynamicImages: gamemodeImages.skyblock?.map(img => img.image_url) || [],
    },
    {
      icon: <Hammer className="w-6 h-6" />,
      title: t('features.3.title'),
      description: t('features.3.description'),
      startDate: t('features.3.startDate'),
      image: gamemode3,
      dynamicImages: gamemodeImages.creative?.map(img => img.image_url) || [],
    },
  ];

  return (
    <section id="features" className="py-24 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimatedSection animation="fade-down" className="text-center mb-24">
          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary-400 to-yellow-300 text-transparent bg-clip-text">
              {t('features.title')}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </AnimatedSection>

        {/* Features Stack */}
        <div className="space-y-32">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index} 
              {...feature} 
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}